/**
 * MFA リセットセクション
 *
 * ユーザー詳細画面に表示し、認証手段を紛失した利用者の MFA（認証アプリの登録）をリセットする。
 * リセットすると、その利用者の全端末のサインインも失効し、次回サインイン時に認証アプリの登録を求められる。
 * リセットの依頼が本人からのものかは、運用手順に従って事前に確認する。
 */
import { Loader2, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useResetUserMfa } from '../../hooks/use-admin-api';

interface MfaResetSectionProps {
  email: string;
}

type Outcome = { kind: 'success'; resetAt: string } | { kind: 'error'; message: string } | null;

export function MfaResetSection({ email }: MfaResetSectionProps) {
  const resetMfa = useResetUserMfa();
  const [confirming, setConfirming] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  const openDialog = () => {
    setIdentityVerified(false);
    setOutcome(null);
    setConfirming(true);
  };

  const handleReset = async () => {
    try {
      const result = await resetMfa.mutateAsync(email);
      setOutcome({ kind: 'success', resetAt: result.resetAt });
      setConfirming(false);
    } catch (error) {
      setOutcome({
        kind: 'error',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border mb-8">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-2">
        <ShieldOff className="w-5 h-5" />
        MFA（認証アプリ）
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        スマートフォンの紛失などで認証アプリを使えなくなった利用者の登録を解除します。利用者は次回のサインインで、認証アプリを登録し直します。
      </p>

      <button
        type="button"
        onClick={openDialog}
        className="px-3 py-2 text-sm border border-red-500/40 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
      >
        MFA をリセット
      </button>

      {outcome?.kind === 'success' && (
        <p role="status" className="mt-3 text-sm text-green-500">
          MFA をリセットしました（{new Date(outcome.resetAt).toLocaleString('ja-JP')}）。
        </p>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mfa-reset-title"
            className="w-full max-w-md rounded-xl border border-border bg-card p-6 space-y-4"
          >
            <h3 id="mfa-reset-title" className="text-lg font-semibold text-foreground">
              MFA をリセットしますか？
            </h3>
            <dl className="text-sm">
              <dt className="text-muted-foreground">対象</dt>
              <dd className="font-medium text-foreground break-all">{email}</dd>
            </dl>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>認証アプリの登録を解除し、全端末のサインインを失効させます。</li>
              <li>
                依頼が本人からのものか、運用手順に従って本人確認を済ませてから実行してください。
              </li>
            </ul>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={identityVerified}
                onChange={(e) => setIdentityVerified(e.target.checked)}
              />
              本人確認を済ませました
            </label>
            {outcome?.kind === 'error' && (
              <p role="alert" className="text-sm text-red-400">
                リセットに失敗しました：{outcome.message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={resetMfa.isPending}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={!identityVerified || resetMfa.isPending}
                className="px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetMfa.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'リセットする'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
